import React, {useCallback, useState} from 'react';
import _ from 'underscore';

import {trackEvent} from 'analytics/Analytics';
import CalendarActions from 'calendar/CalendarActions';
import {CalendarFile} from 'calendar/CalendarConstants';
import CalendarStore from 'calendar/CalendarStore';
import {HOMEPAGE_ANALYTICS_ACTIONS} from 'constants/analytics-products/HomepageAnalyticsConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import useOnMount from 'ui-library/hooks/useOnMount';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';
import fuzzydate from 'util/fuzzydate';

import CardSection, {
  CardSectionEmptyState,
  CardSectionLoadingRows,
  CardSectionMessageRow,
  CardSectionRowList,
  CardSectionRowProps,
} from './CardSection';
import {
  COMPACT_CARD_SECTION_MAX_WIDTH,
  HOMEPAGE_SECTION_TEST_COMPONENT,
  INITIAL_ROWS_TO_DISPLAY,
  LOADING_SKELETON_ROW_COUNT,
  MAX_ROWS_TO_DISPLAY,
} from './HomepageConstants';
import {HOMEPAGE_SECTION_EMPTY_STATES} from './HomepageEmptyStates';
import {getHomepageFileOutlineIcon} from './HomepageIconUtil';
import {useIsSmallerThan} from './useIsSmallerThan';

const TEST_COMPONENT = HOMEPAGE_SECTION_TEST_COMPONENT.RECENTS;

const MAX_DAYS_BACK = 7;

/** Lists recently viewed notebook files from the calendar store for quick reopen. */
export default function RecentsSection(): JSX.Element {
  const todayDate = useStoreSelector(
    CalendarStore,
    useCallback((store) => store.getToday(), [])
  );
  const files = useStoreSelector(
    CalendarStore,
    useCallback((store) => store.getFiles(), [])
  );

  const [showMore, setShowMore] = useState(false);

  const {isSmallerThan, ref} = useIsSmallerThan(COMPACT_CARD_SECTION_MAX_WIDTH);

  useOnMount(() => {
    _.defer(() => {
      const filesStartDate = todayDate.clone().subtract(MAX_DAYS_BACK, 'days');
      const filesEndDate = todayDate.clone().add(2, 'days');
      CalendarActions.fetchFiles(filesStartDate, filesEndDate);
    });
  });

  let recentFiles: Array<CalendarFile> = [];

  for (let i = 0; i < MAX_DAYS_BACK; i++) {
    const lastDate = todayDate.clone().subtract(i, 'days').format('YYYY-MM-DD');
    const filesForDate = files[lastDate];
    if (filesForDate == null) {
      break;
    }
    recentFiles = _.unique([...recentFiles, ...filesForDate], 'id');
  }

  const pickUpSectionRows: Array<CardSectionRowProps> = _.unique(recentFiles, 'id')
    .sort((a, b) => b.endAccess.localeCompare(a.endAccess))
    .slice(0, showMore ? MAX_ROWS_TO_DISPLAY : INITIAL_ROWS_TO_DISPLAY)
    .map((file) => ({
      id: file.id,
      icon: getHomepageFileOutlineIcon(file),
      name: file.name,
      onClick: () => {
        trackEvent(HOMEPAGE_ANALYTICS_ACTIONS.CLICKED_RECENT_FILE);
        FileBrowserActions.openFile(file.id, {
          itemState: {isRegistered: false, isBiologicsOwned: file.isBiologicsOwned},
        });
        SidepanelActions.close({autoOpenFile: false});
      },
      rightContent: file.endAccess
        ? `${isSmallerThan ? '' : 'Viewed '}${fuzzydate(file.endAccess, {showYear: false, showWeekday: true, compact: isSmallerThan})}`
        : null,
    }));

  return (
    <CardSection ref={ref} title="Jump back in" dataTestAttrs={{component: TEST_COMPONENT, element: 'root'}}>
      {_.isEmpty(files) && <CardSectionLoadingRows count={LOADING_SKELETON_ROW_COUNT} />}
      {!_.isEmpty(files) && pickUpSectionRows.length === 0 && (
        <CardSectionEmptyState
          {...HOMEPAGE_SECTION_EMPTY_STATES.jumpBackIn}
          dataTestAttrs={{component: TEST_COMPONENT, element: 'messageRow', key: 'empty'}}
        />
      )}
      {!_.isEmpty(files) && pickUpSectionRows.length > 0 && <CardSectionRowList rows={pickUpSectionRows} />}
      {recentFiles.length > INITIAL_ROWS_TO_DISPLAY && (
        <CardSectionMessageRow
          message={showMore ? 'Show less' : 'Show more'}
          onClick={() => {
            setShowMore(!showMore);
          }}
        />
      )}
    </CardSection>
  );
}
